/**
 * Semantic Action Mapper Singleton
 * Initializes once during app startup for optimal performance
 */

import {
	initializeSemanticMapper,
	registerActionTemplates,
} from './semanticActionMapper';
import {
	generateActionTemplatesFromMenu,
	getStandaloneModalTemplates,
} from './menuActionTemplateGenerator';
import allActionTemplates from './actionTemplates';
import { canvasActionTemplates } from './canvasActionTemplates';
import { editorActionTemplates } from './editorActionTemplates';
import DynamicMenuList from '@navigation/Menu/Components/DynamicMenuList';
import {
	analyzeActionRelationships,
	getSuggestedSequence,
} from './reduxTypesAnalyzer';

class SemanticActionMapperManager {
	private static instance: SemanticActionMapperManager;
	private isInitialized = false;
	private isInitializing = false;
	private initPromise: Promise<void> | null = null;

	private constructor() {
		// Private constructor for singleton
	}

	public static getInstance(): SemanticActionMapperManager {
		if (!SemanticActionMapperManager.instance) {
			SemanticActionMapperManager.instance =
				new SemanticActionMapperManager();
		}
		return SemanticActionMapperManager.instance;
	}

	/**
	 * Initialize the semantic action mapper
	 * This should be called once during app initialization
	 */
	public async initialize(): Promise<void> {
		// If already initialized, return immediately
		if (this.isInitialized) {
			console.log('✅ Semantic action mapper already initialized');
			return;
		}

		// If currently initializing, wait for that to complete
		if (this.isInitializing && this.initPromise) {
			console.log('⏳ Waiting for existing initialization...');
			return this.initPromise;
		}

		// Start initialization
		this.isInitializing = true;
		this.initPromise = this._doInitialize();

		try {
			await this.initPromise;
		} catch (error) {
			this.isInitializing = false;
			throw error;
		}
	}

	private async _doInitialize(): Promise<void> {
		try {
			// Analyze Redux types to find action relationships
			if (__DEV__) {
				console.log('🔍 Analyzing Redux action types...');
				const relationships = analyzeActionRelationships();
				console.log(
					`📊 Found ${relationships.length} action relationships`
				);
			}

			// Generate templates from menu structure (static analysis)
			const menuGeneratedTemplates =
				generateActionTemplatesFromMenu(DynamicMenuList);
			const standaloneTemplates = getStandaloneModalTemplates();

			// Combine templates with priority:
			// 1. Manual templates (highest priority - they have schema and data extraction)
			// 2. Canvas templates (image/canvas operations)
			// 3. Editor templates (rich text editor operations)
			// 4. Standalone templates (modals not in menu)
			// 5. Menu-generated templates (only if not manually defined)
			const combinedTemplates = [
				...allActionTemplates, // Manual templates take priority
				...canvasActionTemplates, // Canvas action templates
				...editorActionTemplates, // Editor action templates
				...standaloneTemplates.filter((template) => {
					// Only include standalone if not manually defined
					const isManuallyDefined = allActionTemplates.some(
						(manualTemplate) =>
							manualTemplate.intent === template.intent
					);
					return !isManuallyDefined;
				}),
				...menuGeneratedTemplates.filter((template) => {
					// Only include menu-generated if not manually or standalone defined
					const isAlreadyDefined =
						allActionTemplates.some(
							(t) => t.intent === template.intent
						) ||
						standaloneTemplates.some(
							(t) => t.intent === template.intent
						);
					return !isAlreadyDefined;
				}),
			];

			// Validate all templates have required fields
			const invalidTemplates = combinedTemplates.filter(
				(template) =>
					!template.intent || template.intent === 'undefined'
			);
			if (invalidTemplates.length > 0) {
				console.error(
					'❌ Found invalid templates with undefined intent:'
				);
				invalidTemplates.forEach((template, index) => {
					console.error(`  ${index + 1}. Phrases:`, template.phrases);
					console.error(`     Category:`, template.category);
					console.error(`     Intent:`, template.intent);
				});
				throw new Error(
					`Found ${invalidTemplates.length} templates with undefined intent. Check Redux action types in menuActionTemplateGenerator.ts or actionTemplates.ts`
				);
			}

			// Register all templates
			registerActionTemplates(combinedTemplates);

			// Initialize the TensorFlow.js model (or fallback to keyword matching)
			await initializeSemanticMapper();

			this.isInitialized = true;
			this.isInitializing = false;

			// Only log success in development
			if (__DEV__) {
				console.log(
					'✅ Semantic Action Mapper initialized with',
					combinedTemplates.length,
					'templates'
				);
			}
		} catch (error) {
			console.error(
				'❌ Failed to initialize semantic action mapper:',
				error
			);
			// Still mark as initialized to allow fallback mode
			this.isInitialized = true;
			this.isInitializing = false;
			throw error;
		}
	}

	/**
	 * Check if the mapper is ready to use
	 */
	public isReady(): boolean {
		return this.isInitialized;
	}

	/**
	 * Get initialization status
	 */
	public getStatus(): {
		isInitialized: boolean;
		isInitializing: boolean;
	} {
		return {
			isInitialized: this.isInitialized,
			isInitializing: this.isInitializing,
		};
	}
}

// Export singleton instance
export const semanticActionMapperManager =
	SemanticActionMapperManager.getInstance();

// Convenience function for initialization
export const initializeSemanticActionMapper = () => {
	return semanticActionMapperManager.initialize();
};

// Convenience function to check if ready
export const isSemanticActionMapperReady = () => {
	return semanticActionMapperManager.isReady();
};
